import { useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  RewriteEventDescriptionCommandInput,
  RewriteEventDescriptionCommandOutput,
  GenerateDescriptionTipsCommandInput,
  GenerateDescriptionTipsCommandOutput,
  GenerateShortDescriptionCommandInput,
  GenerateShortDescriptionCommandOutput,
} from '@amzn/red-velvet-api';
import { useFeatures, getEnabledFeatures } from '../features';
import { getClient } from '../../utils/getClient';
import { getHttpStatusCode, isAbortError } from '../../utils/errorHelper';

/**
 * A slow model call ends up here, and trying again costs the coordinator another long wait for the
 * same likely answer. Better to show an error and let them press the button again.
 */
const NON_RETRYABLE_STATUSES = [500, 502, 504];

/**
 * The generated client retries a transient failure three times on its own, which multiplies with the
 * retry below and can send a single click to the model up to nine times. One attempt per call leaves
 * `retryTransient` as the only thing deciding whether to try again.
 */
const SINGLE_ATTEMPT = { maxAttempts: 1 };

type Cancellable<TInput> = TInput & { abortSignal?: AbortSignal };

function retryTransient(failureCount: number, error: Error): boolean {
  if (failureCount >= 2) return false;
  // The coordinator cancelled, so there is nothing left to wait for.
  if (isAbortError(error)) return false;
  const status = getHttpStatusCode(error);
  if (status !== undefined && NON_RETRYABLE_STATUSES.includes(status)) return false;
  return status === undefined || status >= 500;
}

export function useRewriteEventDescription() {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams), SINGLE_ATTEMPT);

  return useMutation<
    RewriteEventDescriptionCommandOutput,
    Error,
    Cancellable<RewriteEventDescriptionCommandInput>
  >({
    mutationKey: ['RedVelvetApi', 'ai', 'rewriteEventDescription'],
    mutationFn: ({ abortSignal, ...input }) =>
      client.rewriteEventDescription(input, { abortSignal }),
    retry: retryTransient,
  });
}

export function useGenerateDescriptionTips() {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams), SINGLE_ATTEMPT);

  return useMutation<
    GenerateDescriptionTipsCommandOutput,
    Error,
    Cancellable<GenerateDescriptionTipsCommandInput>
  >({
    mutationKey: ['RedVelvetApi', 'ai', 'generateDescriptionTips'],
    mutationFn: ({ abortSignal, ...input }) =>
      client.generateDescriptionTips(input, { abortSignal }),
    retry: retryTransient,
  });
}

export function useGenerateShortDescription() {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams), SINGLE_ATTEMPT);

  return useMutation<
    GenerateShortDescriptionCommandOutput,
    Error,
    Cancellable<GenerateShortDescriptionCommandInput>
  >({
    mutationKey: ['RedVelvetApi', 'ai', 'generateShortDescription'],
    mutationFn: ({ abortSignal, ...input }) =>
      client.generateShortDescription(input, { abortSignal }),
    retry: retryTransient,
  });
}
